import { Autocomplete, Skeleton, TextField, Typography } from "@mui/material";
import { FieldLabel } from "./FieldLabel";
import { invalidControlSx } from "./invalidControlSx";

export type ReferenceOption<T> = {
  id: string;
  label: string;
  title?: string;
  value: T;
};

export const ReferenceSelect = <T,>({
  label,
  value,
  onChange,
  disabled,
  required,
  outstanding,
  invalid,
  options,
  isLoading,
  isError,
  helperText,
  noOptionsText,
}: {
  label: string;
  value: ReferenceOption<T> | null;
  options: ReferenceOption<T>[];
  onChange?: (value: T | null) => void;
  disabled?: boolean;
  required?: boolean;
  outstanding?: string;
  invalid?: boolean;
  isLoading: boolean;
  isError: boolean;
  helperText?: string;
  noOptionsText: string;
}) => {
  const ids = options.map((option) => option.id);
  const autocompleteOptions =
    value && !ids.includes(value.id) ? [value, ...options] : options;
  if (!onChange)
    return isLoading ? (
      <Skeleton width={160} />
    ) : (
      <Typography variant="body1" className="whitespace-pre-wrap">
        {value?.label ?? "Not provided"}
      </Typography>
    );
  return (
    <Autocomplete
      fullWidth
      size="small"
      value={value}
      options={autocompleteOptions}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.id}
      getOptionDisabled={(option) => !ids.includes(option.id)}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, next) => onChange(next?.value ?? null)}
      disabled={disabled || isLoading || isError}
      loading={isLoading}
      noOptionsText={noOptionsText}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            <div className="flex min-w-0 flex-col break-words">
              <Typography variant="body1">
                {option.title ?? option.label}
              </Typography>
              {option.title && (
                <Typography variant="caption" color="text.secondary">
                  {option.label}
                </Typography>
              )}
            </div>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={
            <FieldLabel
              edit
              label={label}
              required={required}
              outstanding={outstanding}
            />
          }
          inputProps={{
            ...params.inputProps,
            "aria-label": label,
            "aria-invalid": invalid || undefined,
          }}
          InputLabelProps={{ required: false }}
          error={isError}
          sx={invalid ? invalidControlSx : undefined}
          helperText={helperText}
        />
      )}
    />
  );
};
